import SiteImage, { optimizedSource } from "./SiteImage";
import { getRoute, pagePath, serviceSlugs, updateMetadata } from "./seo";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CONTACT_INITIAL_STATE,
  submitContactForm,
  validateContactForm,
} from "./contactForm";

const services = [
  {
    title: "Website Hosting",
    text: "Offering reliable, high-performance hosting solutions with 24/7 support to ensure your site's uptime and speed.",
    icon: "db",
    image: "/Images/service7.jpg",
    details: [
      "High-availability hosting with performance monitoring.",
      "Security hardening, SSL, and regular backups.",
      "24/7 support with proactive incident response.",
    ],
  },
  {
    title: "Web Development",
    text: "Crafting robust and scalable websites to enhance your business's online presence and user experience.",
    icon: "code",
    image: "/Images/service8.jpg",
    details: [
      "Modern responsive UI with clean, maintainable code.",
      "Performance, SEO, and accessibility best practices.",
      "Integrations, APIs, and scalable architecture.",
    ],
  },
  {
    title: "Website Design",
    text: "Designing visually engaging and intuitive websites that reflect your brand and provide a seamless user experience.",
    icon: "design",
    details: [
      "Brand-aligned UI/UX for web and mobile.",
      "Design systems, components, and consistent layouts.",
      "Prototypes and iterations focused on conversion.",
    ],
  },
  {
    title: "Cloud Consultancy",
    text: "Providing expert cloud consultancy across AWS, Google Cloud, and Azure to optimize your cloud infrastructure.",
    icon: "cloud",
    details: [
      "Cloud architecture for scalability, security, and cost.",
      "Migration planning and execution support.",
      "Reliability, observability, and governance guidance.",
    ],
  },
  {
    title: "Project Management",
    text: "Clear planning and delivery for every release.",
    details: [
      "Milestone planning and sprint execution.",
      "Clear communication and stakeholder reporting.",
      "Risk management with predictable delivery.",
    ],
  },
  {
    title: "CRM Management",
    text: "Better customer tracking, retention, and insights.",
    details: [
      "CRM setup, workflows, and automation.",
      "Data cleanup and reporting dashboards.",
      "Retention journeys and customer segmentation.",
    ],
  },
];

function ServiceDetailPage({ service, image }) {
  if (!service) return null;
  const isCloudConsultancy =
    (service?.title ?? "").toLowerCase() === "cloud consultancy";
  const isWebDevelopment =
    (service?.title ?? "").toLowerCase() === "web development";
  const serviceKey = (service?.title ?? "").trim().toLowerCase();
  const isWebsiteHosting = serviceKey === "website hosting";
  const isWebsiteDesign = serviceKey === "website design";
  const isProjectManagement = serviceKey === "project management";
  const isCrmManagement = serviceKey === "crm management";

  return (
    <section className="inner-page service-detail-page">
      <div className="service-detail-hero">
        <div className="service-detail-hero-media" aria-hidden="true">
          <SiteImage
            src={image}
            alt=""
            loading="eager"
            sizes="100vw"
            fetchpriority="high"
          />
        </div>
        <div className="container service-detail-hero-inner">
          <h1 className="service-detail-hero-title">{service.title}</h1>
          <p className="service-detail-hero-subtitle">{service.text}</p>
        </div>
      </div>

      <div className="container service-detail-body">
        <div className="service-detail-card">
          {isCloudConsultancy ? (
            <>
              <h2 className="service-detail-card-title">Cloud Consultancy</h2>
              <p className="service-detail-lead">
                Cloud computing is at the core of modern business
                transformation. At Cloudtrim, we help organizations design,
                implement, and manage scalable cloud solutions using leading
                platforms like Amazon Web Services (AWS), Google Cloud, and
                Microsoft Azure.
              </p>
              <p className="service-detail-lead">
                Our cloud consultancy services are focused on improving
                performance, reducing costs, and ensuring your infrastructure is
                secure, reliable, and future-ready.
              </p>

              <h3 className="service-detail-section-title">What We Offer</h3>
              <ol className="service-detail-offers">
                <li>
                  <strong>Cloud Strategy &amp; Planning</strong>
                  <p>
                    We analyze your current systems and business goals to create
                    a customized cloud adoption strategy. Whether you're
                    starting fresh or optimizing existing infrastructure, we
                    guide you with the right roadmap.
                  </p>
                </li>
                <li>
                  <strong>Cloud Migration Services</strong>
                  <p>
                    Seamlessly move your applications, databases, and workloads
                    to the cloud with minimal downtime. We ensure a smooth and
                    secure transition from on-premise or legacy systems.
                  </p>
                </li>
                <li>
                  <strong>Infrastructure Design &amp; Architecture</strong>
                  <p>
                    We design scalable, high-performance cloud architectures
                    tailored to your business needs. Our solutions ensure
                    flexibility, fault tolerance, and long-term growth.
                  </p>
                </li>
                <li>
                  <strong>Cost Optimization</strong>
                  <p>
                    Avoid unnecessary cloud expenses with smart resource
                    allocation, usage monitoring, and cost-control strategies.
                    We help you get the best value from your cloud investment.
                  </p>
                </li>
                <li>
                  <strong>Security &amp; Compliance</strong>
                  <p>
                    We implement industry best practices to protect your data
                    and applications. From identity management to encryption and
                    compliance standards, we keep your cloud environment secure.
                  </p>
                </li>
              </ol>
            </>
          ) : isWebDevelopment ? null : isWebsiteHosting ? (
            <>
              <section
                className="service-rich-intro"
                aria-label="Website hosting overview"
              >
                <h2>Website Hosting</h2>
                <p>
                  Reliable hosting is the foundation of a great website.
                  Cloudtrim provides high‑performance hosting with security,
                  monitoring, and support—so your site stays fast and available
                  as your traffic grows.
                </p>
                <ul className="service-rich-bullets">
                  <li>High uptime with proactive monitoring</li>
                  <li>SSL, backups, and security hardening</li>
                  <li>Performance tuning and caching for speed</li>
                </ul>
              </section>

              <section
                className="service-rich-work"
                aria-label="Hosting features"
              >
                <h2>Hosting Features</h2>
                <div className="service-rich-steps" role="list">
                  {[
                    {
                      title: "Performance Optimization",
                      text: "We ensure your website delivers fast load times with advanced caching, CDN integration, and optimized server configurations—improving both user experience and SEO.",
                    },
                    {
                      title: "Scalability on Demand",
                      text: "As your traffic grows, your hosting scales seamlessly. No downtime, no performance drops—just consistent, reliable growth.",
                    },
                    {
                      title: "Cloud Infrastructure",
                      text: "Built on modern cloud platforms, our hosting ensures flexibility, high availability, and enterprise-grade reliability.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="service-rich-step"
                      role="listitem"
                    >
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section
                className="service-rich-intro"
                aria-label="What makes our hosting different"
              >
                <h2>What Makes Our Hosting Different</h2>
                <ul className="service-rich-bullets">
                  <li>
                    Fully managed hosting—no technical effort required from your
                    side
                  </li>
                  <li>
                    Optimized for modern web applications and business websites
                  </li>
                  <li>
                    Proactive issue detection and resolution before it impacts
                    users
                  </li>
                  <li>Transparent performance monitoring and reporting</li>
                  <li>Designed to support growth from startup to scale</li>
                </ul>
              </section>

              <section
                className="service-rich-cards"
                aria-label="What you get with hosting"
                role="list"
              >
                {[
                  {
                    title: "Managed Infrastructure",
                    text: "We handle setup, updates, and maintenance so you can focus on your business.",
                    image: "/Images/backend.jpg",
                  },
                  {
                    title: "Security & Backups",
                    text: "SSL, hardened configurations, and regular backups with restore support.",
                    image: "/Images/service4.jpg",
                  },
                  {
                    title: "Support & Monitoring",
                    text: "24/7 monitoring and quick response to keep your site healthy and online.",
                    image: "/Images/service7.jpg",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="service-rich-card"
                    role="listitem"
                  >
                    <div className="service-rich-card-media" aria-hidden="true">
                      <SiteImage src={item.image} alt="" loading="lazy" />
                    </div>
                    <div className="service-rich-card-body">
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </section>
            </>
          ) : isWebsiteDesign ? (
            <>
              <section
                className="service-rich-intro"
                aria-label="Website design overview"
              >
                <h2>Website Design</h2>
                <p>
                  Great design builds trust and makes it easy for customers to
                  take action. We create modern, brand‑aligned websites that
                  look beautiful, feel intuitive, and support conversion.
                </p>
                <ul className="service-rich-bullets">
                  <li>Brand‑consistent UI with strong visual hierarchy</li>
                  <li>Mobile‑first layouts and accessibility best practices</li>
                  <li>Conversion‑focused pages and clear content structure</li>
                </ul>
              </section>

              <section
                className="service-rich-intro"
                aria-label="Design that drives results"
              >
                <h2>Design That Drives Results</h2>
                <p>
                  We don’t just design for aesthetics—we design for outcomes.
                  Every layout, interaction, and visual element is crafted to
                  guide users, build trust, and increase conversions.
                </p>
              </section>

              <section
                className="service-rich-cards"
                aria-label="Design deliverables"
                role="list"
              >
                {[
                  {
                    title: "UI/UX Design",
                    text: "Wireframes and polished layouts that guide users smoothly to outcomes.",
                    image: "/Images/service3.jpg",
                  },
                  {
                    title: "Design System",
                    text: "Reusable components, styles, and rules so your design stays consistent.",
                    image: "/Images/service8.jpg",
                  },
                  {
                    title: "Prototype & Review",
                    text: "Clickable prototypes and iterations so you can approve with confidence.",
                    image: "/Images/service2.jpg",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="service-rich-card"
                    role="listitem"
                  >
                    <div className="service-rich-card-media" aria-hidden="true">
                      <SiteImage src={item.image} alt="" loading="lazy" />
                    </div>
                    <div className="service-rich-card-body">
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </section>

              <section
                className="service-rich-work"
                aria-label="What we focus on"
              >
                <h2>What We Focus On</h2>
                <div className="service-rich-steps" role="list">
                  {[
                    {
                      title: "User-Centered Design",
                      text: "We design with your users in mind—ensuring intuitive navigation, clear messaging, and seamless interactions.",
                    },
                    {
                      title: "Conversion Optimization",
                      text: "Layouts and content structures are optimized to turn visitors into leads or customers.",
                    },
                    {
                      title: "Performance & Accessibility",
                      text: "Designs follow accessibility standards and are optimized for fast loading and smooth experiences across devices.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="service-rich-step"
                      role="listitem"
                    >
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="service-rich-intro" aria-label="What you get">
                <h2>What You Get</h2>
                <ul className="service-rich-bullets">
                  <li>Pixel-perfect, developer-ready design files</li>
                  <li>Consistent design system for scalability</li>
                  <li>Mobile-first, responsive layouts</li>
                  <li>Clear content hierarchy for better engagement</li>
                  <li>Design assets optimized for performance and SEO</li>
                </ul>
              </section>

              <section
                className="service-rich-work"
                aria-label="Design process"
              >
                <h2>Our Process</h2>
                <div className="service-rich-steps" role="list">
                  {[
                    {
                      title: "Discovery",
                      text: "Understand goals, users, and content needs.",
                    },
                    {
                      title: "Wireframes",
                      text: "Structure pages and user flows for clarity.",
                    },
                    {
                      title: "Visual Design",
                      text: "Apply brand, typography, and UI details.",
                    },
                    {
                      title: "Handoff",
                      text: "Deliver assets/specs ready for development.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="service-rich-step"
                      role="listitem"
                    >
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  ))}
                </div>
              </section>
            </>
          ) : isProjectManagement ? (
            <>
              <section
                className="service-rich-intro"
                aria-label="Project management overview"
              >
                <h2>Project Management</h2>
                <p>
                  Structured execution with clear visibility at every stage.
                </p>
                <p>
                  We ensure your projects are delivered on time, within scope,
                  and aligned with your business goals. Through transparent
                  communication, organized workflows, and disciplined execution,
                  we keep every stakeholder informed and every release
                  predictable.
                </p>
                <ul className="service-rich-bullets">
                  <li>
                    Clear milestone planning and structured sprint execution
                  </li>
                  <li>
                    Transparent communication with regular progress updates
                  </li>
                  <li>Proactive risk management and scope control</li>
                </ul>
              </section>

              <section
                className="service-rich-intro"
                aria-label="Delivering projects with confidence"
              >
                <h2>Delivering Projects with Confidence</h2>
                <p>
                  At Cloudtrim, we combine proven project management practices
                  with agile delivery to maintain momentum and reduce
                  uncertainty. From planning to launch, we focus on clarity,
                  accountability, and consistent outcomes.
                </p>
              </section>

              <section
                className="service-rich-cards"
                aria-label="How we manage delivery"
                role="list"
              >
                {[
                  {
                    title: "Planning & Roadmaps",
                    text: "Define project scope, timelines, and milestones aligned with your business priorities and long-term goals.",
                    image: "/Images/service1.jpg",
                  },
                  {
                    title: "Execution & Reporting",
                    text: "Manage sprints, track progress, and provide clear, consistent updates to keep teams and stakeholders aligned.",
                    image: "/Images/service6.jpg",
                  },
                  {
                    title: "Quality & Release Management",
                    text: "Coordinate testing, approvals, and deployment to ensure smooth, reliable releases.",
                    image: "/Images/service5.jpg",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="service-rich-card"
                    role="listitem"
                  >
                    <div className="service-rich-card-media" aria-hidden="true">
                      <SiteImage src={item.image} alt="" loading="lazy" />
                    </div>
                    <div className="service-rich-card-body">
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </section>

              <section
                className="service-rich-work"
                aria-label="Engagement workflow"
              >
                <h2>Engagement Workflow</h2>
                <div className="service-rich-steps" role="list">
                  {[
                    {
                      title: "Kickoff",
                      text: "Align on goals, roles, timelines, and success metrics to establish a strong foundation.",
                    },
                    {
                      title: "Delivery Rhythm",
                      text: "Execute in structured sprints with regular demos and feedback loops to maintain progress and flexibility.",
                    },
                    {
                      title: "Risk Management",
                      text: "Identify dependencies, track risks, and implement mitigation strategies proactively.",
                    },
                    {
                      title: "Launch & Handover",
                      text: "Ensure seamless deployment, complete documentation, and a smooth transition to operations.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="service-rich-step"
                      role="listitem"
                    >
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section
                className="service-rich-intro"
                aria-label="What you get with project management"
              >
                <h2>What You Get</h2>
                <ul className="service-rich-bullets">
                  <li>
                    Predictable delivery timelines and structured execution
                  </li>
                  <li>Clear visibility into progress and milestones</li>
                  <li>Reduced risks through proactive planning</li>
                  <li>Strong coordination between teams and stakeholders</li>
                  <li>Smooth, well-managed product releases</li>
                </ul>
              </section>
            </>
          ) : isCrmManagement ? (
            <>
              <section
                className="service-rich-intro"
                aria-label="CRM management overview"
              >
                <h2>CRM Management</h2>
                <p>
                  Gain complete visibility into your customer lifecycle and
                  sales pipeline with a structured, data-driven CRM approach. At
                  Cloudtrim, we help businesses design, implement, and optimize
                  CRM systems that streamline operations, improve data accuracy,
                  and enhance customer engagement.
                </p>
                <p>
                  Our focus is on creating intelligent workflows, actionable
                  reporting, and scalable automation—enabling your sales and
                  support teams to operate efficiently with reliable, real-time
                  insights.
                </p>
              </section>

              <section
                className="service-rich-work"
                aria-label="Core capabilities"
              >
                <h2>Core Capabilities</h2>
              </section>

              <section
                className="service-rich-cards"
                aria-label="CRM core capabilities"
                role="list"
              >
                {[
                  {
                    title: "CRM Setup & Automation",
                    text: "We design and implement customized CRM systems tailored to your business processes. From pipeline configuration to workflow automation, we ensure your CRM works seamlessly to reduce manual effort and improve team productivity.",
                    image: "/Images/service4.jpg",
                  },
                  {
                    title: "Workflow Automation",
                    text: "Automate repetitive tasks such as lead assignment, follow-ups, notifications, and reminders. This ensures faster response times, improved consistency, and better conversion rates.",
                    image: "/Images/service3.jpg",
                  },
                  {
                    title: "Reporting & Dashboards",
                    text: "Build intuitive dashboards that provide clear visibility into pipeline performance, sales conversions, and key metrics. Our reporting solutions enable data-driven decision-making at every level.",
                    image: "/Images/backend.jpg",
                  },
                  {
                    title: "Data Management & Governance",
                    text: "We enhance data quality through structured organization, cleanup, and segmentation. By establishing strong governance practices, we ensure your CRM data remains accurate, secure, and actionable.",
                    image: "/Images/service7.jpg",
                  },
                  {
                    title: "Customer Retention & Insights",
                    text: "Leverage CRM data to design targeted customer journeys, improve retention strategies, and gain deeper insights into customer behavior and engagement patterns.",
                    image: "/Images/backend.jpg",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="service-rich-card"
                    role="listitem"
                  >
                    <div className="service-rich-card-media" aria-hidden="true">
                      <SiteImage src={item.image} alt="" loading="lazy" />
                    </div>
                    <div className="service-rich-card-body">
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </section>

              <section
                className="service-rich-work"
                aria-label="CRM implementation steps"
              >
                <h2>Implementation Approach</h2>
                <div className="service-rich-steps" role="list">
                  {[
                    {
                      title: "Audit",
                      text: "We assess your existing CRM setup, data quality, workflows, and operational challenges to identify gaps and opportunities for improvement.",
                    },
                    {
                      title: "Design",
                      text: "We define optimized pipelines, data structures, workflows, and automation rules aligned with your business objectives.",
                    },
                    {
                      title: "Configuration",
                      text: "We implement and configure CRM systems, including pipelines, user roles, permissions, integrations, and dashboards.",
                    },
                    {
                      title: "Adoption & Optimization",
                      text: "We provide training, onboarding, and continuous improvements to ensure consistent usage, high adoption rates, and long-term success.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="service-rich-step"
                      role="listitem"
                    >
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="service-rich-intro" aria-label="CRM closing">
                <p className="service-rich-closing">
                  Transform your CRM into a powerful growth engine with
                  structured processes, automation, and actionable insights.
                </p>
              </section>
            </>
          ) : (
            <>
              <h2 className="service-detail-card-title">About this service</h2>
              <p className="service-detail-lead">{service.text}</p>

              {Array.isArray(service.details) && service.details.length > 0 ? (
                <ul className="service-detail-list">
                  {service.details.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              ) : null}
            </>
          )}
        </div>

        {isWebDevelopment ? (
          <>
            <section
              className="web-detail-intro"
              aria-label="Web development overview"
            >
              <h2>About This Service</h2>
              <p>
                We design and build scalable, high-performance web applications
                that strengthen your digital presence and deliver seamless user
                experiences. Every solution is crafted with clean code, modern
                design, and a focus on long-term growth.
              </p>
              <ul className="web-detail-bullets">
                <li>
                  Responsive, user-friendly interfaces built for all devices
                </li>
                <li>Optimized for performance, SEO, and accessibility</li>
                <li>Secure integrations, APIs, and scalable architecture</li>
              </ul>

              <h3 className="web-detail-kicker">
                Building Web Products That Scale
              </h3>
              <p>
                At Cloudtrim, we partner with businesses to create modern web
                applications that are fast, reliable, and built to grow. By
                combining product thinking with strong engineering, we help you
                launch confidently and evolve continuously.
              </p>
            </section>

            <section
              className="web-detail-services"
              aria-label="Web development services"
            >
              <div className="web-detail-services-grid">
                <h2 className="web-detail-section-title">Core Capabilities</h2>
                <div className="web-detail-cards" role="list">
                  {[
                    {
                      title: "UI/UX & Product Design",
                      text: "We design intuitive user experiences through clear user journeys, wireframes, and scalable design systems—focused on usability and conversion.",
                      image: "/Images/service3.jpg",
                    },
                    {
                      title: "Frontend Development",
                      text: "We build responsive, high-performance interfaces using clean, reusable components for a seamless experience across all devices.",
                      image: "/Images/service8.jpg",
                    },
                    {
                      title: "Backend Development",
                      text: "We develop secure, scalable backend systems with robust APIs and integrations—ensuring reliability and production-ready performance.",
                      image: "/Images/backend.jpg",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="web-detail-card"
                      role="listitem"
                    >
                      <div className="web-detail-card-media" aria-hidden="true">
                        <SiteImage src={item.image} alt="" loading="lazy" />
                      </div>
                      <div className="web-detail-card-body">
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section
              className="web-detail-stack"
              aria-label="Full stack solutions"
            >
              <div className="web-detail-stack-inner">
                <h2>Solutions We Deliver</h2>
                <div className="web-detail-stack-grid" role="list">
                  {[
                    {
                      title: "Web Portals",
                      text: "Centralized dashboards and portals that simplify workflows, user management, and reporting.",
                    },
                    {
                      title: "Websites",
                      text: "Fast, SEO-optimized websites designed to clearly communicate your brand and drive engagement.",
                    },
                    {
                      title: "Ecommerce Development",
                      text: "End-to-end ecommerce solutions with secure payments and optimized user journeys to increase conversions.",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="web-detail-stack-item"
                      role="listitem"
                    >
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section className="web-detail-sprints" aria-label="Sprint process">
              <h2>How We Work</h2>
              <div className="web-detail-sprints-grid" role="list">
                {[
                  {
                    title: "Discovery",
                    text: "We understand your business goals, users, and requirements to define the right approach.",
                  },
                  {
                    title: "Design & Prototype",
                    text: "We create wireframes and prototypes to visualize and refine the user experience.",
                  },
                  {
                    title: "Build & Test",
                    text: "We develop scalable solutions with continuous testing to ensure quality and performance.",
                  },
                  {
                    title: "Launch",
                    text: "We deploy secure, stable products ready to perform and scale in real-world environments.",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="web-detail-sprint"
                    role="listitem"
                  >
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                ))}
              </div>
            </section>
          </>
        ) : null}

        {isCloudConsultancy ? (
          <>
            <section
              className="cloud-detail-block"
              aria-label="Cloud consultancy offerings"
            >
              <div className="cloud-detail-grid">
                <div className="cloud-detail-intro">
                  <h2>Our Services</h2>
                  <p>
                    We help you design, build, and run reliable cloud
                    platforms—from strategy and migration to operational
                    excellence.
                  </p>
                </div>

                <div className="cloud-detail-cards" role="list">
                  {[
                    {
                      title: "Cloud Consultancy",
                      text: "Strategic guidance across AWS, Google Cloud, and Azure to design, optimize, and scale your cloud infrastructure.",
                      image: "/Images/backend.jpg",
                    },
                    {
                      title: "DevOps & Automation",
                      text: "CI/CD pipelines, infrastructure as code, and automation to accelerate development and ensure reliable deployments.",
                      image: "/Images/service4.jpg",
                    },
                    {
                      title: "Cloud Monitoring & Reliability",
                      text: "24/7 monitoring, performance optimization, and incident management to keep your systems secure, fast, and always available.",
                      image: "/Images/service3.jpg",
                    },
                  ].map((item) => (
                    <div
                      key={item.title}
                      className="cloud-detail-card"
                      role="listitem"
                    >
                      <div
                        className="cloud-detail-card-media"
                        aria-hidden="true"
                      >
                        <SiteImage src={item.image} alt="" loading="lazy" />
                      </div>
                      <div className="cloud-detail-card-body">
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            <section
              className="cloud-detail-partners"
              aria-label="Our ecosystem partnerships"
            >
              <h3 className="home-partners-title">Cloud Expertise</h3>
              <div
                className="home-partners-logos"
                aria-label="Cloud platforms we work with"
              >
                <a
                  className="home-partner-card"
                  aria-label="Microsoft Azure"
                  href="https://azure.microsoft.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <SiteImage
                    className="home-partner-logo"
                    src="/Images/Azure_logo.png"
                    alt="Azure"
                    loading="lazy"
                  />
                </a>

                <a
                  className="home-partner-card"
                  aria-label="Amazon Web Services"
                  href="https://aws.amazon.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <SiteImage
                    className="home-partner-logo"
                    src="/Images/aws_logo.png"
                    alt="AWS"
                    loading="lazy"
                  />
                </a>

                <a
                  className="home-partner-card"
                  aria-label="Google Cloud"
                  href="https://cloud.google.com/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <SiteImage
                    className="home-partner-logo"
                    src="/Images/Google_cloud.png"
                    alt="Google Cloud"
                    loading="lazy"
                  />
                </a>
              </div>
            </section>
          </>
        ) : null}

        <div className="service-detail-back-bottom">
          <a className="btn btn-pill" href={"/services/"}>
            Back to Services
            <span className="btn-pill-arrow" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                <path d="M5 12h12" />
                <path d="m13 6 6 6-6 6" />
              </svg>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
}

const homeServiceImages = [
  "/Images/service1.jpg",
  "/Images/service2.jpg",
  "/Images/service3.jpg",
  "/Images/service4.jpg",
];

const allServiceImages = [
  ...homeServiceImages,
  "/Images/service5.jpg",
  "/Images/service6.jpg",
];

function ValuesCarousel({ items }) {
  const scrollerRef = useRef(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  const sync = () => {
    const el = scrollerRef.current;
    if (!el) return;
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    setCanPrev(el.scrollLeft > 2);
    setCanNext(el.scrollLeft < maxScrollLeft - 2);
  };

  useEffect(() => {
    const handle = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(handle);
  }, [items?.length]);

  const scrollByCards = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const card = el.querySelector(".home-value-card");
    const cardWidth = card?.getBoundingClientRect?.().width ?? 280;
    const gap = 16;
    const delta = (cardWidth + gap) * 1.15 * direction;
    el.scrollBy({ left: delta, behavior: "smooth" });
    window.setTimeout(sync, 250);
  };

  return (
    <div className="home-values-wrap">
      <div
        className="home-values-row"
        tabIndex={0}
        role="region"
        ref={scrollerRef}
        onScroll={sync}
        aria-label="Our Values"
      >
        {items.map((item) => (
          <article key={item.title} className="home-value-card">
            <div className="home-value-media" aria-hidden="true">
              <SiteImage src={item.image} alt="" loading="lazy" />
            </div>
            <div className="home-value-body">
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </div>
          </article>
        ))}
      </div>

      <div className="home-values-nav" aria-label="Values navigation">
        <button
          type="button"
          className="home-values-arrow"
          onClick={() => scrollByCards(-1)}
          disabled={!canPrev}
          aria-label="Previous values"
        >
          ‹
        </button>
        <button
          type="button"
          className="home-values-arrow"
          onClick={() => scrollByCards(1)}
          disabled={!canNext}
          aria-label="Next values"
        >
          ›
        </button>
      </div>
    </div>
  );
}

function RevealedWords({ text, className = "" }) {
  return <span className={className}>{text}</span>;
}

function Header({ page }) {
  const items = ["home", "about", "services", "careers", "blog", "contact"];
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const close = (event) => {
      if (event.key === "Escape") {
        setMobileOpen(false);
        menuRef.current?.focus();
      }
    };
    document.addEventListener("keydown", close);
    return () => document.removeEventListener("keydown", close);
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [page]);

  return (
    <header className="site-header">
      <div className="container nav-row">
        <a className="logo" href={pagePath("home")}>
          <SiteImage
            src="/Images/Logo.png"
            loading="eager"
            sizes="160px"
            alt="Cloudtrim Technologies LLP"
          />
        </a>
        <nav className="nav" aria-label="Primary navigation">
          {items.map((item) => (
            <a
              key={item}
              className={page === item ? "active" : ""}
              aria-current={page === item ? "page" : undefined}
              href={pagePath(item)}
            >
              {item === "about"
                ? "About Us"
                : item[0].toUpperCase() + item.slice(1)}
            </a>
          ))}
        </nav>
        <div className="nav-actions">
          <a className="btn btn-primary nav-contact" href={pagePath("contact")}>
            Contact
          </a>
          <button
            type="button"
            ref={menuRef}
            className="nav-menu"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-controls="mobile-navigation"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
          >
            <span aria-hidden="true">☰</span>
          </button>
        </div>
      </div>

      {mobileOpen ? (
        <div
          id="mobile-navigation"
          className="mobile-nav"
          role="navigation"
          aria-label="Mobile navigation"
        >
          <div className="container mobile-nav-inner">
            {items.map((item) => (
              <a
                key={`m-${item}`}

                className={`mobile-nav-link ${page === item ? "is-active" : ""}`}
                href={pagePath(item)}
              >
                {item === "about"
                  ? "About Us"
                  : item[0].toUpperCase() + item.slice(1)}
              </a>
            ))}
          </div>
        </div>
      ) : null}
    </header>
  );
}

function SiteFooter() {
  const quickLinks = [
    { label: "Home", page: "home" },
    { label: "About", page: "about" },
    { label: "Services", page: "services" },
    { label: "Careers", page: "careers" },
    { label: "Blog", page: "blog" },
    { label: "Contact", page: "contact" },
  ];

  return (
    <footer className="footer">
      <div className="container footer-grid">
        <div>
          <div className="footer-brand">
            <div className="footer-logo">
              <SiteImage
                src="/Images/logo-transparent-png-4.png"
                alt="Cloudtrim"
              />
            </div>
            <div className="footer-links" aria-label="Quick links">
              <h2 className="footer-links-title">Quick Links</h2>
              <div className="footer-links-row">
                {quickLinks.map((item) => (
                  <a
                    key={item.page}

                    className="footer-link"
                    href={pagePath(item.page)}
                  >
                    {item.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div>
          <h2>Corporate Address</h2>
          <p>Main Office: 452/B, Balaji Heights, Vita, Maharashtra 415311</p>
          <p>Pune Branch: Akshara School Lane, Wakad, Maharashtra 411057</p>
        </div>
        <div className="footer-contact">
          <h2 className="footer-contact-title" aria-label="Get In Touch">
            {["Get", "In", "Touch"].map((word, index) => (
              <span key={word} style={{ "--word-delay": `${index * 0.16}s` }}>
                {word}
              </span>
            ))}
          </h2>
          <p>
            <a href="mailto:info@cloudtrim.in">info@cloudtrim.in</a>
          </p>
          <p>
            <a href="tel:+912067620186">+91 2067620186</a>
          </p>
          <div className="social-links">
            <a
              href="https://www.facebook.com/sharer.php?u=https://cloudtrim.in/"
              target="_blank"
              rel="noreferrer"
              aria-label="Share on Facebook"
            >
              f
            </a>
            <a
              href="https://x.com/intent/post?url=https%3A%2F%2Fcloudtrim.in%2F"
              target="_blank"
              rel="noreferrer"
              aria-label="Share on X"
            >
              X
            </a>
          </div>
        </div>
      </div>
      <div className="copy">(c)2024 Created with Cloudtrim LLP</div>
    </footer>
  );
}

function HomePage() {
  const [activeServiceIndex, setActiveServiceIndex] = useState(0);
  const [activeHeroVideo, setActiveHeroVideo] = useState(0);
  const heroVideos = ["/videos/background2.mp4", "/videos/background3.mp4"];
  const [videoSrc, setVideoSrc] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVideoSrc(heroVideos[activeHeroVideo]);
    }, 50);
    return () => clearTimeout(timer);
  }, [activeHeroVideo]);

  return (
    <>
      <section className="hero">
        <div className="hero-media" aria-hidden="true">
          <video
            key={activeHeroVideo}
            className="hero-poster"
            src={videoSrc || undefined}
            poster="/Images/hero-poster.webp"
            autoPlay
            muted
            playsInline
            preload="none"
            onEnded={() => setActiveHeroVideo((index) => (index + 1) % heroVideos.length)}
          />
        </div>
        <div className="container hero-grid">
          <div className="hero-copy">
            <h1>
              Building the Future of <span>Your Online</span>
              <RevealedWords
                text="Presence With Cloudtrim"
                className="hero-title-reveal"
              />
            </h1>
            <p>
              Expert web development, design, and cloud consulting for
              unstoppable business momentum.
            </p>
          </div>
        </div>
      </section>

      <section className="home-about-teaser">
        <div className="container">
          <div className="home-about-teaser-inner">
            <div className="home-about-teaser-media" aria-hidden="true">
              <SiteImage
                src="/Images/pexels-shoper-pl-550490863-17485350.jpg"
                alt=""
                loading="lazy"
              />
            </div>
            <div className="home-about-teaser-main">
              <div className="home-about-teaser-copy">
                <h2>About Us</h2>
                <p>
                  Cloudtrim is a modern technology partner dedicated to helping
                  businesses succeed in an increasingly digital world. With
                  expertise spanning web development, cloud consultancy, project
                  management, and digital strategy, we deliver end-to-end
                  solutions that address real business challenges.
                </p>
                <p>
                  Our approach is built on a deep understanding of both
                  technology and business needs. We don’t just develop
                  solutions—we create scalable, secure, and high-performance
                  digital experiences that are designed to evolve with your
                  growth. From building robust web platforms to optimizing cloud
                  infrastructure, we ensure every solution is aligned with
                  long-term success.
                </p>
                <p>
                  At Cloudtrim, we believe in combining clean engineering,
                  intuitive design, and reliable execution to deliver measurable
                  outcomes. Our team works closely with clients to understand
                  their goals, identify opportunities, and implement solutions
                  that drive efficiency, improve user engagement, and maximize
                  return on investment.
                </p>
              </div>
              <div className="home-about-teaser-actions">
                <a className="btn btn-primary" href={pagePath("about")}>
                  About Cloudtrim
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="home-partners home-customers">
        <div className="container">
          <h3 className="home-partners-title">Our Customers</h3>
          <div
            className="home-partners-logos home-partners-logos--customers"
            aria-label="Customers we work with"
          >
            <div
              className="home-customer-logo home-customer-logo--taskwatch home-customer-logo--taskwatch-mark"
              aria-label="TaskWatch"
            >
              <SiteImage
                className="home-partner-logo"
                src="/Images/taskwatch-mark.png"
                alt="TaskWatch"
                loading="lazy"
              />
            </div>
            <div
              className="home-customer-logo home-customer-logo--taskwatch"
              aria-label="TaskWatch"
            >
              <SiteImage
                className="home-partner-logo"
                src="/Images/taskwatch-wordmark.png"
                alt="TaskWatch"
                loading="lazy"
              />
            </div>
            <div
              className="home-customer-logo home-customer-logo--sgg"
              aria-label="Sanjay Ghodawat Group"
            >
              <SiteImage
                className="home-partner-logo"
                src="/Images/sanjay-ghodawat-group.png"
                alt="Sanjay Ghodawat Group"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="services-teaser home-services-teaser">
        <div className="container">
          <h2 className="home-services-title">Our Services</h2>
        </div>

        <div className="services-page-wide">
          <div className="services-tabs" role="group" aria-label="Services">
            {services.map((item, index) => (
              <button
                key={item.title}
                type="button"

                aria-pressed={index === activeServiceIndex}
                className={`services-tab ${index === activeServiceIndex ? "is-active" : ""}`}
                onClick={() => setActiveServiceIndex(index)}
              >
                {item.title}
              </button>
            ))}
          </div>

          <article className="services-feature">
            <div className="services-feature-media">
              <SiteImage
                src={
                  services[activeServiceIndex]?.image ??
                  allServiceImages[activeServiceIndex] ??
                  allServiceImages[0]
                }
                alt={services[activeServiceIndex]?.title ?? "Service"}
                loading="lazy"
              />
            </div>
            <div className="services-feature-content">
              <h2>
                <span>Discover The Difference</span>
                <span>With {services[activeServiceIndex]?.title}</span>
              </h2>
              <p>{services[activeServiceIndex]?.text}</p>
              <a
                className="btn btn-pill"
                href={"/services/" + serviceSlugs[activeServiceIndex] + "/"}
              >
                Explore {services[activeServiceIndex]?.title}
                <span className="btn-pill-arrow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                    <path d="M5 12h12" />
                    <path d="m13 6 6 6-6 6" />
                  </svg>
                </span>
              </a>
            </div>
          </article>
        </div>
      </section>

      <section className="home-partners">
        <div className="container">
          <h3 className="home-partners-title">Cloud Expertise</h3>
          <div
            className="home-partners-logos"
            aria-label="Cloud platforms we work with"
          >
            <a
              className="home-partner-link"
              aria-label="Microsoft Azure"
              href="https://azure.microsoft.com/"
              target="_blank"
              rel="noreferrer"
            >
              <SiteImage
                className="home-partner-logo"
                src="/Images/Azure_logo.png"
                alt="Azure"
                loading="lazy"
              />
            </a>

            <a
              className="home-partner-link"
              aria-label="Amazon Web Services"
              href="https://aws.amazon.com/"
              target="_blank"
              rel="noreferrer"
            >
              <SiteImage
                className="home-partner-logo"
                src="/Images/aws_logo.png"
                alt="AWS"
                loading="lazy"
              />
            </a>

            <a
              className="home-partner-link"
              aria-label="Google Cloud"
              href="https://cloud.google.com/"
              target="_blank"
              rel="noreferrer"
            >
              <SiteImage
                className="home-partner-logo"
                src="/Images/Google_cloud.png"
                alt="Google Cloud"
                loading="lazy"
              />
            </a>
          </div>
        </div>
      </section>

      <section className="about-talk about-talk--full about-talk--tall">
        <div className="container">
          <div className="about-talk-grid">
            <div className="about-talk-copy">
              <h2>Let&apos;s talk!</h2>
              <p>
                Send us some details about your project, and feel free to ask
                questions about our process. Our consultants will be with you
                ASAP.
              </p>
              <a className="btn btn-primary about-talk-cta" href="/contact/">
                <span>Contact Us</span>
                <span className="about-talk-cta-arrow" aria-hidden="true">
                  →
                </span>
              </a>
            </div>
            <div className="about-talk-media" aria-hidden="true">
              <SiteImage
                src="/Images/lets-talk-home.png"
                alt=""
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function AboutPage() {
  return (
    <section className="inner-page about-page">
      <div className="container">
        <div className="about-hero">
          <div className="about-hero-grid">
            <div className="about-hero-copy">
              <h1 className="about-hero-title">
                A world that&apos;s{" "}
                <span className="about-hero-accent">yours</span> to build
              </h1>
              <p className="about-hero-subtitle">
                Cloudtrim creates modern web and cloud solutions with clean
                engineering, smart design, and dependable delivery—so your
                business can grow without limits.
              </p>
            </div>

            <div className="about-hero-media" aria-hidden="true">
              <SiteImage
                src="/Images/pexels-shoper-pl-550490863-17485350.jpg"
                alt=""
                loading="lazy"
              />
            </div>
          </div>
        </div>

        <section className="home-about-block">
          <div className="home-about-layout">
            <div className="home-about-row">
              <div className="home-about-label">
                <h2>Who we are.</h2>
              </div>
              <div className="home-about-content">
                <div className="home-about-copy">
                  <p>
                    Cloudtrim is a modern technology partner enabling businesses
                    to succeed in an increasingly digital landscape. Backed by a
                    team of skilled professionals, we bring deep expertise
                    across web development, cloud consultancy, project
                    management, and digital strategy.
                  </p>
                  <p>
                    We are driven by a commitment to deliver scalable, secure,
                    and high-performing solutions built on industry best
                    practices. By aligning technology with business goals, we
                    help organizations unlock efficiency, enhance user
                    experiences, and achieve sustainable growth.
                  </p>
                </div>
              </div>
            </div>

            <div className="home-about-row">
              <div className="home-about-label">
                <h2>Our Vision.</h2>
              </div>
              <div className="home-about-content">
                <div className="home-about-copy">
                  <p>
                    Our vision is to be a globally recognized technology partner
                    known for excellence, innovation, and impact.
                  </p>
                  <p>
                    We aim to set new standards in digital solution delivery by
                    combining clean engineering, intuitive design, and
                    consistent execution—creating measurable value for every
                    client we serve.
                  </p>
                  <p className="home-about-muted">
                    We uphold our vision by our guiding values.
                  </p>
                </div>
              </div>
            </div>

            <div className="home-about-row">
              <div className="home-about-label">
                <h2>Our Values.</h2>
                <p className="home-about-values-note">
                  Our values define who we are and guide how we work,
                  collaborate, and deliver.
                </p>
              </div>
              <div className="home-about-content">
                <ValuesCarousel
                  items={[
                    {
                      title: "Exceed Client Expectations",
                      text: "Delivering quality-driven solutions that go beyond expectations and build lasting partnerships.",
                      image: "/Images/value-exceed.png",
                    },
                    {
                      title: "Humility, Trust, and Respect",
                      text: "Acting with integrity, transparency, and respect in every interaction.",
                      image: "/Images/value-humility.png",
                    },
                    {
                      title: "Collaboration and Support",
                      text: "Working together to achieve seamless execution and shared success.",
                      image: "/Images/value-collaboration.png",
                    },
                    {
                      title: "Curiosity and Innovation",
                      text: "Continuously learning, exploring new ideas, and driving innovation.",
                      image: "/Images/value-curiosity.png",
                    },
                    {
                      title: "Empathy and Compassion",
                      text: "We believe in being empathetic and compassionate. We recognize that acts of compassion and respect go a long way.",
                      image: "/Images/value-empathy.png",
                    },
                  ]}
                />
              </div>
            </div>

            <div className="home-about-row">
              <div className="home-about-label">
                <h2>What we do best.</h2>
              </div>
              <div className="home-about-content">
                <div className="home-about-copy">
                  <p>
                    Cloudtrim supports businesses at every stage of their
                    digital journey—from strategic consulting to delivering
                    production-ready solutions.
                  </p>
                  <p>
                    We begin by understanding your business objectives,
                    challenges, and growth vision. Based on these insights, we
                    design and implement cost-effective, scalable solutions
                    tailored to your needs.
                  </p>
                  <p>
                    Our expertise spans web development, cloud solutions,
                    project management, CRM systems, and reliable
                    hosting—ensuring optimized performance, enhanced user
                    experiences, and maximum return on investment.
                  </p>
                  <p>
                    With a strong focus on innovation, efficiency, and
                    execution, we help businesses move faster, operate smarter,
                    and scale with confidence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <section className="about-talk about-talk--full">
        <div className="container">
          <div className="about-talk-grid">
            <div className="about-talk-copy">
              <h2>Let&apos;s talk!</h2>
              <p>
                Send us some details about your project, and feel free to ask
                questions about our process. Our consultants will be with you
                ASAP.
              </p>
              <a className="btn btn-primary about-talk-cta" href="/contact/">
                <span>Contact Us</span>
                <span className="about-talk-cta-arrow" aria-hidden="true">
                  →
                </span>
              </a>
            </div>
            <div
              className="about-talk-media about-talk-media--contain"
              aria-hidden="true"
            >
              <SiteImage
                src="/Images/lets-talk-about.png"
                alt=""
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </section>
  );
}

function ServicesPage() {
  return (
    <section className="inner-page services-page">
      <div className="container">
        <h1 className="page-title">Services</h1>
      </div>

      <div className="services-page-wide">
        <section className="services-cardstrip" aria-label="Services gallery">
          <div className="services-cardstrip-head">
            <h2 className="services-cardstrip-title">Explore our services</h2>
            <p>
              From websites and cloud infrastructure to delivery and customer
              operations, find the support your business needs.
            </p>
          </div>

          <div className="services-cardstrip-track">
            {services.map((service, index) => {
              const image =
                service?.image ??
                allServiceImages[index] ??
                allServiceImages[0];

              return (
                <a
                  key={`${service.title}-${index}`}

                  className="services-cardstrip-card"

                  style={{ backgroundImage: `url(${optimizedSource(image)})` }}
                  aria-label={`Open ${service.title}`}
                  href={"/services/" + serviceSlugs[index] + "/"}
                >
                  <span
                    className="services-cardstrip-card-arrow"
                    aria-hidden="true"
                  >
                    ↗
                  </span>
                  <span className="services-cardstrip-card-title">
                    {service.title}
                  </span>
                </a>
              );
            })}
          </div>
        </section>
      </div>
    </section>
  );
}

const officeLocations = {
  main: {
    label: "Main Office",
    switchLabel: "Pune Office",
    address:
      "452/B, Balaji Heights,Vivekananda Nagar, Vita, Maharashtra 415311",
    mapLink:
      "https://www.google.com/maps/place/Vivekanand+Nagar,+Bhavaninagar,+Vita,+Maharashtra+415311/",
    embed:
      "https://maps.google.com/maps?q=Cloudtrim%20Technologies%20LLP%2C%20452%2FB%2C%20Balaji%20Heights%2C%20Vitananda%20Nagar%2C%20Vita%2C%20Maharashtra%20415311&z=16&output=embed",
  },
  pune: {
    label: "Pune Office",
    switchLabel: "Main Office",
    address: "Sr no 107, Akshara school, Wakad, Maharashtra 411057",
    mapLink:
      "https://www.google.com/maps/search/?api=1&query=Sr%20no%20107%2C%20Akshara%20school%2C%20Wakad%2C%20Maharashtra%20411057",
    embed:
      "https://maps.google.com/maps?q=Sr%20no%20107%2C%20Akshara%20school%2C%20Wakad%2C%20Maharashtra%20411057&z=15&output=embed",
  },
};

function ContactPage() {
  const [form, setForm] = useState(CONTACT_INITIAL_STATE);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");

  const locations = [
    {
      key: "main",
      label: "Main Office",
      address: officeLocations.main.address,
      mapLink: officeLocations.main.mapLink,
      position: { top: "64%", left: "46%" },
    },
    {
      key: "pune",
      label: "Pune Office",
      address: officeLocations.pune.address,
      mapLink: officeLocations.pune.mapLink,
      position: { top: "40%", left: "60%" },
    },
  ];

  function handleChange(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();

    const validationErrors = validateContactForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      document.getElementById(Object.keys(validationErrors)[0])?.focus();
      setStatus("error");
      return;
    }

    setStatus("submitting");
    setErrors({});

    try {
      await submitContactForm(form);
      setStatus("success");
      setForm(CONTACT_INITIAL_STATE);
    } catch (error) {
      if (error.errors) {
        setErrors(error.errors);
      } else {
        setErrors({ form: error.message || "Unable to submit your message." });
      }
      setStatus("error");
    }
  }

  return (
    <section className="contact-page-new">
      <header className="contact-hero">
        <div className="contact-hero-bg" aria-hidden="true">
          <SiteImage
            className="contact-hero-bg-image"
            src="/Images/contact-hero.png"
            alt=""
            loading="eager"
          />
        </div>
        <div className="container contact-hero-grid">
          <div className="contact-hero-copy">
            <h1 className="contact-hero-loop" aria-label="Get in touch with us">
              {["Get", "in", "touch", "with", "us"].map((word, index) => (
                <span key={word} style={{ "--word-delay": `${index * 0.16}s` }}>
                  {word}
                </span>
              ))}
            </h1>
          </div>

          <form
            className="contact-form contact-form-hero"
            onSubmit={handleSubmit}
            noValidate
          >
            <div className="contact-form-copy">
              <p className="contact-eyebrow">Contact us</p>
            </div>

            <div className="contact-honeypot" aria-hidden="true">
              <label htmlFor="company">Company</label>
              <input
                id="company"
                name="company"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.company}
                onChange={handleChange}
              />
            </div>

            <div className="contact-grid">
              <div className="field-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="Enter your First Name"
                  value={form.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                  maxLength={100}
                  required
                  aria-invalid={Boolean(errors.firstName)}
                  aria-describedby={
                    errors.firstName ? "firstName-error" : undefined
                  }
                />

                {errors.firstName ? (
                  <span
                    id="firstName-error"
                    className="field-error"
                    role="alert"
                  >
                    {errors.firstName}
                  </span>
                ) : null}
              </div>
              <div className="field-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Enter Your Last Name"
                  value={form.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                  maxLength={100}
                  required
                  aria-invalid={Boolean(errors.lastName)}
                  aria-describedby={
                    errors.lastName ? "lastName-error" : undefined
                  }
                />

                {errors.lastName ? (
                  <span
                    id="lastName-error"
                    className="field-error"
                    role="alert"
                  >
                    {errors.lastName}
                  </span>
                ) : null}
              </div>
              <div className="field-group field-group-full">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email Address"
                  value={form.email}
                  onChange={handleChange}
                  autoComplete="email"
                  maxLength={255}
                  required
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={errors.email ? "email-error" : undefined}
                />

                {errors.email ? (
                  <span id="email-error" className="field-error" role="alert">
                    {errors.email}
                  </span>
                ) : null}
              </div>
              <div className="field-group field-group-full">
                <label htmlFor="phone">Phone Number *</label>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={handleChange}
                  autoComplete="tel"
                  maxLength={20}
                  required
                  aria-invalid={Boolean(errors.phone)}
                  aria-describedby={errors.phone ? "phone-error" : undefined}
                />

                {errors.phone ? (
                  <span id="phone-error" className="field-error" role="alert">
                    {errors.phone}
                  </span>
                ) : null}
              </div>
            </div>

            <div className="field-group field-group-full">
              <label htmlFor="message">Your Message *</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                placeholder="Your Message"
                value={form.message}
                onChange={handleChange}
                minLength={10}
                maxLength={5000}
                required
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "message-error" : undefined}
              />

              {errors.message ? (
                <span id="message-error" className="field-error" role="alert">
                  {errors.message}
                </span>
              ) : null}
            </div>

            {errors.form ? (
              <p className="field-error field-error-form" role="alert">
                {errors.form}
              </p>
            ) : null}

            {status === "success" ? (
              <p
                className="contact-form-note contact-form-success"
                role="status"
              >
                Thank you! We received your message and will get back to you
                soon.
              </p>
            ) : null}

            <button
              type="submit"
              className="btn btn-primary small contact-submit"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? "Sending..." : "Submit Form"}
            </button>
          </form>
        </div>
      </header>

      <div className="contact-section-gap" aria-hidden="true" />

      <section className="contact-worldmap contact-worldmap--full">
        <div
          className="contact-worldmap-stage"
          aria-label="Office locations map"
        >
          <SiteImage
            className="contact-worldmap-bg"
            src="/Images/map.png"
            alt=""
            aria-hidden="true"
            loading="lazy"
          />

          {locations.map((loc) => (
            <a
              key={loc.key}
              className="contact-worldmap-card"
              href={
                loc.mapLink ||
                `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
                  loc.address,
                )}&travelmode=driving`
              }
              style={loc.position}
              aria-label={`Open ${loc.label} location in maps`}
            >
              <p className="contact-worldmap-card-title">{loc.label}</p>
              <p className="contact-worldmap-card-address">{loc.address}</p>
            </a>
          ))}
        </div>
      </section>
    </section>
  );
}

function CareersPage() {
  const [activeReasonIndex, setActiveReasonIndex] = useState(0);
  const careerReasons = [
    {
      title: "Challenges",
      text: "Solve meaningful technical problems for real businesses.",
      image: "/Images/careers-reason-01.png",
    },
    {
      title: "Continuous Learning",
      text: "Learn fast with the right tools, reviews, and support.",
      image: "/Images/careers-reason-02.png",
    },
    {
      title: "Flexibility",
      text: "Work in a way that supports your productivity and well-being.",
      image: "/Images/careers-reason-03.png",
    },
    {
      title: "Next-gen Technologies",
      text: "Build modern web and cloud solutions using current best practices.",
      image: "/Images/careers-reason-04.png",
    },
    {
      title: "Balance",
      text: "Find the right balance between delivery and life outside work.",
      image: "/Images/careers-reason-05.png",
    },
    {
      title: "Having Fun",
      text: "Be part of a team that enjoys building and shipping together.",
      image: "/Images/careers-reason-06.png",
    },
  ];

  const activeReason = careerReasons[activeReasonIndex] ?? careerReasons[0];

  return (
    <section className="careers-page">
      <div className="careers-hero">
        <SiteImage
          className="careers-hero-end-photo"
          src="/Images/careers-challenges.png"
          alt=""
          loading="lazy"
          aria-hidden="true"
        />

        <div className="container careers-hero-content">
          <h1>
            Ready to help build
            <br />
            stronger businesses?{" "}
            <span className="careers-hero-join">Join us.</span>
          </h1>
          <a className="btn btn-primary" href={pagePath("contact")}>
            Contact Us
          </a>
        </div>
      </div>

      <div className="container careers-mission">
        <h2>Become Part of Our Mission</h2>
        <p className="careers-mission-subtitle">
          Our mission is to build a world of amazing digital experiences.
        </p>
        <div className="careers-mission-copy">
          <p>
            Our teams drive that mission forward through thoughtful engineering,
            cloud consulting, and product-focused design. We help businesses
            solve real operational challenges with reliable, scalable, and
            secure solutions.
          </p>
          <p>
            Join us to build modern web platforms, optimize cloud
            infrastructure, and deliver experiences that customers love. If
            you’d like to apply, share your resume and portfolio — we’d love to
            hear from you.
          </p>
        </div>
      </div>

      <section className="careers-reasons">
        <div className="container careers-reasons-grid">
          <div className="careers-reasons-left">
            <h2>What makes Cloudtrim a great place to work?</h2>
            <div className="careers-reasons-list">
              {careerReasons.map((item, index) => (
                <button
                  key={item.title}
                  type="button"
                  className={`careers-reason ${index === activeReasonIndex ? "is-active" : ""}`}
                  onClick={() => setActiveReasonIndex(index)}
                >
                  <div className="careers-reason-number" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </div>
                  <div className="careers-reason-copy">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <aside className="careers-reasons-right" aria-hidden="true">
            <div className="careers-reasons-photo">
              <SiteImage src={activeReason?.image} alt="" loading="lazy" />
            </div>
          </aside>
        </div>
      </section>

      <section className="careers-purpose">
        <div className="container careers-purpose-grid">
          <div className="careers-purpose-left">
            <h2>Find your purpose.</h2>
            <p>
              Our core values are what set us apart. They’re the guide lights
              that unite us, and make us what we are as a people-first
              organization.
            </p>
          </div>
          <div className="careers-purpose-right">
            <ul className="careers-purpose-list">
              {[
                "Exceed Client’s Expectations",
                "Humbleness, Trust, and Respect",
                "Collaboration and Support",
                "Curiosity and Innovation",
                "Empathy and Compassion",
              ].map((item) => (
                <li key={item}>
                  <span className="careers-check" aria-hidden="true" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="careers-cta">
        <div className="container careers-cta-grid">
          <div className="careers-cta-left">
            <h2>
              Your opportunity to take on the most exciting challenges of your
              work life begins here.
            </h2>
            <a className="careers-cta-button" href={pagePath("contact")}>
              <span>Join our Team</span>
              <span className="careers-cta-arrow" aria-hidden="true">
                →
              </span>
            </a>
          </div>
          <div className="careers-cta-right" aria-hidden="true">
            <SiteImage src="/Images/careers-cta.png" alt="" loading="lazy" />
          </div>
        </div>
      </section>
    </section>
  );
}

const blogPosts = [
  {
    id: "serverless-ai",
    title:
      'The "Quiet" Revolution: How Serverless AI is Democratizing Innovation',
    excerpt:
      "Cloud-native, serverless inference hides heavy infrastructure behind simple APIs—dropping AI’s barrier to entry from GPU clusters to pay-per-use execution.",
    date: "Cloud AI Content Series",
    readMins: 4,
    category: "Serverless AI",
    image: "/Images/service4.jpg",
    author: "Cloudtrim",
    content: [
      { type: "h2", text: "The Hook" },
      {
        type: "p",
        text: 'Until recently, deploying a large language model (LLM) or a complex computer vision system required a massive upfront investment in GPU clusters and specialized DevOps talent. Today, the "Cloud-Native AI" model has flipped the script. We are entering the era of "Invisible Infrastructure," where the complexity of the machine is hidden behind a simple API call.',
      },
      { type: "h2", text: "The Shift to Serverless Inference" },
      {
        type: "p",
        text: "In the traditional model, you rented a virtual machine (VM), scaled it manually, and paid for it even when it sat idle. Serverless AI changes this by abstracting the infrastructure entirely. Using services like AWS Lambda, Google Cloud Run, or specialized platforms like Pinecone for vector databases, developers can now trigger AI workflows based on events.",
      },
      {
        type: "ul",
        items: [
          "Usage-Based Pricing: Some serverless services charge primarily for execution; minimum capacity, storage and other charges depend on the service and configuration.",
          "Managed Scaling: Cloud services can scale with demand within configured quotas, concurrency limits and regional capacity.",
        ],
      },
      { type: "h2", text: 'The Demise of the "Moat"' },
      {
        type: "p",
        text: 'Previously, a company’s "moat" was its hardware. Now, the moat is its data and how creatively it implements AI. Small startups are now out-pacing legacy enterprises because they aren\'t bogged down by "technical debt" or physical server maintenance. They are building "Thin Apps" that leverage "Thick AI" models hosted in the cloud.',
      },
      { type: "h2", text: "The Takeaway" },
      {
        type: "p",
        text: "Cloud isn't just where AI lives; it’s the engine that makes AI affordable. For the modern entrepreneur, the barrier to entry has dropped from millions of dollars to the price of a monthly subscription.",
      },
    ],
  },
  {
    id: "aiops",
    title: 'Beyond the Hype: Building "Self-Healing" Clouds with AIOps',
    excerpt:
      "AIOps shifts operations from reactive alerts to predictive maintenance—detecting anomalies, pinpointing root causes, and automatically remediating issues across complex cloud systems.",
    date: "Cloud AI Content Series",
    readMins: 5,
    category: "AIOps",
    image: "/Images/service7.jpg",
    author: "Cloudtrim",
    content: [
      { type: "h2", text: "The Hook" },
      {
        type: "p",
        text: "The most expensive minute in business is a minute of downtime. As cloud environments grow into multi-cloud, hybrid-mesh architectures, they become too complex for human teams to monitor in real-time. Enter AIOps—the practice of using machine learning to automate IT operations.",
      },
      { type: "h2", text: "From Reactive to Predictive" },
      {
        type: "p",
        text: "Traditional monitoring is reactive: a server hits 95% CPU, an alarm goes off, and an engineer wakes up at 3:00 AM to fix it. AIOps moves the needle toward “Predictive Maintenance.”",
      },
      {
        type: "ul",
        items: [
          'Anomaly Detection: AI learns the "heartbeat" of your application. It can distinguish between a healthy traffic spike (like a Black Friday sale) and a malicious DDoS attack or a memory leak.',
          'Causal Analysis: When a system fails, AI can sift through millions of log lines across dozens of microservices to find the "Patient Zero" of the crash in seconds—a task that would take a human team hours of "war room" debugging.',
        ],
      },
      { type: "h2", text: "The Rise of the Self-Healing System" },
      {
        type: "p",
        text: "The ultimate goal of AIOps is the self-healing cloud. Imagine a scenario where a database latency increases. The AI detects it, realizes it's a regional networking issue, and automatically reroutes traffic to a healthy node in a different geographic zone while simultaneously spinning up a fresh instance—using tested runbooks, approval policies and rollback controls. Operators remain responsible for validating automated actions.",
      },
      { type: "h2", text: "The Takeaway" },
      {
        type: "p",
        text: "AIOps can reduce repetitive operational work. Reliable adoption still requires engineering ownership, monitoring, validated automation and clear escalation paths.",
      },
    ],
  },
  {
    id: "green-cloud",
    title: "The Green Cloud: Can AI Solve Its Own Sustainability Crisis?",
    excerpt:
      "AI is both power-hungry and power-saving: from optimizing data-center cooling to carbon-aware scheduling, it can reduce cloud energy overhead while keeping scale sustainable.",
    date: "Cloud AI Content Series",
    readMins: 5,
    category: "Sustainability",
    image: "/Images/service8.jpg",
    author: "Cloudtrim",
    content: [
      { type: "h2", text: "The Hook" },
      {
        type: "p",
        text: "The irony of the AI boom is its thirst for power. A single query to a generative AI model can consume ten times the electricity of a standard Google search. As organizations rush to adopt AI, they are hitting a wall: Sustainability targets. However, the very technology causing the strain—AI—might also be the solution to the “Green Cloud.”",
      },
      { type: "h2", text: "AI as the Chief Efficiency Officer" },
      {
        type: "p",
        text: "Hyperscale data centers (like those run by Microsoft, Google, and Oracle) are using AI to solve the “Cooling Problem.” Cooling accounts for nearly 40% of a data center's total energy usage.",
      },
      {
        type: "ul",
        items: [
          "DeepMind's 2016 Case Study: Google reported up to 40% less cooling energy and a 15% reduction in overall PUE overhead in its tested system. These historical results are not a universal guarantee.",
          'Carbon-Aware Scheduling: AI can now practice "Time-Shifting." It can identify non-critical tasks (like training a new model or running weekly backups) and schedule them for times when the local power grid is being fed by peak solar or wind energy.',
        ],
      },
      { type: "h2", text: "Circular Economy and Hardware Lifecycle" },
      {
        type: "p",
        text: 'AI is also being used to predict when server hardware is likely to fail, allowing for "just-in-time" replacement. This prevents the waste of premature upgrades and reduces the carbon footprint associated with manufacturing new silicon chips.',
      },
      { type: "h2", text: "The Takeaway" },
      {
        type: "p",
        text: "The “Green Cloud” isn't a marketing slogan; it's a technical necessity. By using AI to optimize every watt of power and every liter of cooling water, we can ensure that the intelligence revolution doesn't come at the cost of the planet.",
      },
    ],
  },
];

function BlogListingPage() {
  return (
    <section className="inner-page blog-page">
      <div className="blog-hero" aria-hidden="true">
        <SiteImage
          className="blog-hero-image"
          src="/Images/blog-hero.png"
          alt=""
          loading="eager"
        />
      </div>
      <div className="container">
        <h1 className="page-title">Blog</h1>
        <p className="contact-page-subtitle">
          Insights, updates, and guides from Cloudtrim.
        </p>

        <section className="blog-grid">
          {blogPosts.map((post) => (
            <article key={post.id} className="blog-card">
              <div className="blog-card-media" aria-hidden="true">
                <SiteImage src={post.image} alt="" loading="lazy" />
                <span className="blog-card-pill">{post.readMins} min read</span>
              </div>
              <div className="blog-card-body">
                <div className="blog-card-meta">
                  <span className="blog-card-date">{post.date}</span>
                  <span className="blog-card-dot" aria-hidden="true">
                    ·
                  </span>
                  <span className="blog-card-cat">{post.category}</span>
                </div>
                <h2 className="blog-card-title">{post.title}</h2>
                <p className="blog-card-excerpt">{post.excerpt}</p>
                <a className="blog-card-link" href={"/blog/" + post.id + "/"}>
                  Read {post.category} article
                </a>
              </div>
            </article>
          ))}
        </section>
      </div>
    </section>
  );
}

function BlogDetailPage({ postId }) {
  const post = blogPosts.find((p) => p.id === postId);
  const latestPosts = blogPosts.slice(0, 3);
  const categories = useMemo(() => {
    const counts = new Map();
    for (const item of blogPosts) {
      const key = item.category || "Other";
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
  }, []);

  if (!post) {
    return (
      <section className="inner-page blog-page">
        <div className="container">
          <a className="btn btn-ghost small" href={"/blog/"}>
            ← Back
          </a>
          <h1 className="page-title">Post not found</h1>
        </div>
      </section>
    );
  }

  return (
    <section className="inner-page blog-detail">
      <div className="blog-detail-cover" aria-hidden="true">
        <SiteImage src={post.image} alt="" loading="eager" sizes="100vw" />
      </div>

      <div className="container blog-detail-container">
        <p className="blog-detail-author">Author: {post.author}</p>
        <h1 className="blog-detail-title">{post.title}</h1>

        <div className="blog-detail-layout">
          <article className="blog-detail-body">
            {post.content.map((block, index) => {
              if (typeof block === "string") {
                return <p key={`${post.id}-${index}`}>{block}</p>;
              }

              if (block?.type === "h2") {
                return (
                  <h2 key={`${post.id}-${index}`} className="blog-detail-h2">
                    {block.text}
                  </h2>
                );
              }

              if (block?.type === "p") {
                return <p key={`${post.id}-${index}`}>{block.text}</p>;
              }

              if (block?.type === "ul") {
                return (
                  <ul key={`${post.id}-${index}`} className="blog-detail-list">
                    {block.items?.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                );
              }

              return null;
            })}
            <nav className="blog-sidebar-social" aria-label="Share this article">
              <a
                className="blog-social-btn"
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent("https://cloudtrim.in/blog/" + post.id + "/")}`}
                aria-label="Share on LinkedIn"
              >
                in
              </a>
              <a
                className="blog-social-btn"
                href={`https://x.com/intent/post?url=${encodeURIComponent("https://cloudtrim.in/blog/" + post.id + "/")}`}
                aria-label="Share on X"
              >
                X
              </a>
              <a
                className="blog-social-btn"
                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://cloudtrim.in/blog/" + post.id + "/")}`}
                aria-label="Share on Facebook"
              >
                f
              </a>
            </nav>
          </article>

          <aside className="blog-detail-sidebar" aria-label="Blog sidebar">
            <div className="blog-sidebar-section">
              <h3 className="blog-sidebar-title">Latest Posts</h3>
              <div className="blog-sidebar-latest">
                {latestPosts.map((item, index) => (
                  <a
                    key={item.id}

                    className="blog-latest-item"
                    href={"/blog/" + item.id + "/"}
                  >
                    <span className="blog-latest-number" aria-hidden="true">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <span className="blog-latest-copy">
                      <span className="blog-latest-title">{item.title}</span>
                      <span className="blog-latest-meta">
                        <span className="blog-latest-link">Read More</span>
                        <span className="blog-latest-date">{item.date}</span>
                      </span>
                    </span>
                  </a>
                ))}
              </div>
            </div>

            <div className="blog-sidebar-section">
              <h3 className="blog-sidebar-title">Categories</h3>
              <div className="blog-sidebar-cats">
                {categories.map((cat) => (
                  <div key={cat.name} className="blog-cat-row">
                    <span className="blog-cat-name">{cat.name}</span>
                    <span className="blog-cat-count">{cat.count}</span>
                  </div>
                ))}
              </div>
            </div>


          </aside>
        </div>

        <div className="blog-detail-footer">
          <a className="btn btn-ghost small blog-detail-back" href={"/blog/"}>
            ← Back to Blog
          </a>
        </div>
      </div>
    </section>
  );
}

function App({ initialPath = "/" }) {
  const route = getRoute(initialPath);
  const page = route.page;

  useEffect(() => {
    updateMetadata(route);
  }, [route]);
  let content;
  if (page === "about") content = <AboutPage />;
  else if (page === "services") content = <ServicesPage />;
  else if (page === "service-detail") {
    const service = services[route.serviceIndex];
    content = (
      <ServiceDetailPage
        service={service}
        image={service.image ?? allServiceImages[route.serviceIndex]}
      />
    );
  } else if (page === "careers") content = <CareersPage />;
  else if (page === "blog") content = <BlogListingPage />;
  else if (page === "blog-detail")
    content = <BlogDetailPage postId={route.postId} />;
  else if (page === "contact") content = <ContactPage />;
  else if (page === "home") content = <HomePage />;
  else
    content = (
      <section className="inner-page container">
        <h1>Page not found</h1>
        <p>
          The page may have moved. Explore our services or contact our team.
        </p>
        <a className="btn btn-primary" href="/services/">
          Explore services
        </a>
      </section>
    );
  return (
    <div className="page">
      <a className="skip-link" href="#main-content">
        Skip to content
      </a>
      <Header page={page} />
      <main id="main-content" tabIndex={-1} className="page-view">
        {content}
      </main>
      <SiteFooter />
    </div>
  );
}
export default App;
